/* eslint-disable no-undef */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import fetchData from "../../functions/fetchData";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { getHeaders } from "../../utility/getHeader";
import Header from "../../components/Header";
import Layout from "../../layout/Layout";
import {
    Card,
    Row,
    Col,
    Button,
    Input,
    InputNumber,
    Select,
    DatePicker,
    Checkbox,
    Table,
    Tag,
    Space,
    Typography,
    Modal,
    Tooltip,
    Statistic,
} from "antd";
import {
    PlusOutlined,
    PrinterOutlined,
    FileExcelOutlined,
    FilePdfOutlined,
    UserOutlined,
    DollarOutlined,
    CreditCardOutlined,
    CalendarOutlined,
    SearchOutlined,
    FileTextOutlined,
} from "@ant-design/icons";
import "./vouchers.css";

const { Text } = Typography;
const { Option } = Select;

export default function Vouchers() {
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState("");
    const [outstandingAmount, setOutstandingAmount] = useState(0);
    const [paymentAmount, setPaymentAmount] = useState("");
    const [paymentMode, setPaymentMode] = useState("");
    const [referenceNumber, setReferenceNumber] = useState("");
    const [remarks, setRemarks] = useState("");
    const [balanceAfterPayment, setBalanceAfterPayment] = useState(0);
    const [data, setData] = useState([]);
    const [companyInfo, setCompanyInfo] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterMode, setFilterMode] = useState("All");
    const [bankAccounts, setBankAccounts] = useState([]);
    const [bankAccountId, setBankAccountId] = useState("");
    const [tdsAmount, setTdsAmount] = useState("");
    const [isTdsReceivable, setIsTdsReceivable] = useState(false);
    const [discountAmount, setDiscountAmount] = useState("");
    const [isDiscountGiven, setIsDiscountGiven] = useState(false);
    const [voucherDate, setVoucherDate] = useState(dayjs());
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Payment modes with bank account requirement
    const paymentModes = [
        { value: "Cash", label: "💵 Cash / เงินสด", requiresBank: false },
        { value: "Bank", label: "🏦 Bank Transfer / โอนเงิน", requiresBank: true },
        { value: "Credit_Card", label: "💳 Credit Card / บัตรเครดิต", requiresBank: true },
        { value: "UPI", label: "📱 UPI / ยูพีไอ", requiresBank: true },
        { value: "Cheque", label: "📝 Cheque / เช็ค", requiresBank: true },
        { value: "Online", label: "💻 Online Payment / จ่ายออนไลน์", requiresBank: true }
    ];
    
    // Fetch customers, bank accounts and company info when component loads
    useEffect(() => {
        fetchData("customers", setCustomers, "id", {});
        fetchBankAccounts();
        fetchCompanyInfo();
    }, []);

    const fetchBankAccounts = async () => {
        try {
            await fetchData("bank_accounts", setBankAccounts, "id", {});
        } catch (error) {
            console.error('Error fetching bank accounts:', error);
        }
    };

    const fetchCompanyInfo = async () => {
        try {
            const companyData = await fetchData("company_profile", null, "id", {});
            if (companyData && companyData.length > 0) {
                setCompanyInfo(companyData[0]);
            }
        } catch (error) {
            console.error("Error fetching company info:", error);
        }
    };
    const getOutStandingBalance = async (customerId) => {
        setSelectedCustomer(customerId); // Update selected customer state

        if (!customerId) {
            setOutstandingAmount(0);
            return;
        }

        try {
            const res = await axios.get(`/customer-ledger-balance/${customerId}`, getHeaders());
            if (res.data.success) {
                // Use the balance from the API response
                const balance = res.data.data.balance || 0;
                setOutstandingAmount(Math.abs(balance)); // Show absolute value as outstanding amount
            } else {
                toast.error("Failed to fetch customer outstanding balance");
            }
        } catch (error) {
            console.error("Error fetching outstanding balance:", error);
            toast.error("Error fetching outstanding balance.");
        }
    };
    useEffect(() => {
        // Calculate balance after payment
        // If TDS is deducted, total payment = amount received + TDS amount
        // If a discount is given, it also clears outstanding balance without any cash/bank inflow
        const payment = parseFloat(paymentAmount) || 0;
        const tds = isTdsReceivable ? (parseFloat(tdsAmount) || 0) : 0;
        const discount = isDiscountGiven ? (parseFloat(discountAmount) || 0) : 0;
        const totalPayment = payment + tds + discount; // Total payment includes received amount, TDS and discount
        const remaining = outstandingAmount - totalPayment;
        setBalanceAfterPayment(remaining >= 0 ? remaining : 0);
    }, [paymentAmount, outstandingAmount, tdsAmount, isTdsReceivable, discountAmount, isDiscountGiven]);

    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        if (!selectedCustomer || !paymentAmount || !paymentMode) {
            toast.error("Please fill in all required fields");
            return;
        }

        // Get customer name from the customers array
        const selectedCustomerData = customers.find(c => c.id === parseInt(selectedCustomer));
        const customerName = selectedCustomerData ? selectedCustomerData.name : 'Unknown Customer';

        const paymentData = {
            customer_id: selectedCustomer,
            customer_name: customerName,
            amount: parseFloat(paymentAmount),
            payment_mode: paymentMode,
            bank_account_id: bankAccountId || null,
            reference_number: referenceNumber,
            remarks: remarks,
            description: remarks,
            receipt_date: voucherDate.format('YYYY-MM-DD'),
            tds_amount: isTdsReceivable ? parseFloat(tdsAmount) || 0 : 0,
            is_tds_receivable: isTdsReceivable,
            discount_amount: isDiscountGiven ? parseFloat(discountAmount) || 0 : 0,
            is_discount_given: isDiscountGiven
        };

        setLoading(true);
        try {
            const res = await axios.post("/receipt-vouchers", paymentData, getHeaders());

            if (res.data.success) {
                toast.success("Receipt voucher created successfully!");
                await fetchData("receipt_vouchers", setData, "id", {});

                // Reset form
                setSelectedCustomer("");
                setPaymentAmount("");
                setPaymentMode("");
                setBankAccountId("");
                setReferenceNumber("");
                setRemarks("");
                setTdsAmount("");
                setIsTdsReceivable(false);
                setDiscountAmount("");
                setIsDiscountGiven(false);
                setOutstandingAmount(0);
                setBalanceAfterPayment(0);
                setVoucherDate(dayjs());
                setShowCreateModal(false);
            } else {
                toast.error(res.data.message || "Failed to create receipt voucher");
            }
        } catch (error) {
            console.error("Error saving receipt:", error);
            toast.error(`Error recording receipt: ${error?.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handlePrintReceipt = async (voucherData) => {
        try {
            setLoading(true);
            
            // Get customer details
            const customer = customers.find(c => c.id === parseInt(voucherData.customer_id));
            const customerName = customer ? customer.name : (voucherData.customer_name || 'N/A');
            const customerAddress = customer ? customer.address || 'N/A' : 'N/A';
            const customerPhone = customer ? customer.phone || 'N/A' : 'N/A';
            
            // Use correct date field and handle invalid dates
            const receiptDate = voucherData.receipt_date || voucherData.payment_date || voucherData.created_at;
            const paymentDate = receiptDate ? new Date(receiptDate) : new Date();
            
            // Check if date is valid
            const isValidDate = !isNaN(paymentDate.getTime());
            
            const formattedDate = isValidDate ? paymentDate.toLocaleDateString('th-TH', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }) : 'วันที่ไม่ระบุ';
            
            const formattedTime = isValidDate ? paymentDate.toLocaleTimeString('th-TH', {
                hour: '2-digit',
                minute: '2-digit'
            }) : 'เวลาไม่ระบุ';

            // Use correct amount field and handle NaN
            const amount = parseFloat(voucherData.amount || voucherData.amount_paid || 0) || 0;
            const tdsAmount = parseFloat(voucherData.tds_amount || 0) || 0;
            const hasTDS = voucherData.is_tds_receivable || tdsAmount > 0;
            const discountAmount = parseFloat(voucherData.discount_amount || 0) || 0;
            const hasDiscount = voucherData.is_discount_given || discountAmount > 0;
            const totalAmount = amount + (hasTDS ? tdsAmount : 0) + (hasDiscount ? discountAmount : 0);
            const amountInText = totalAmount > 0 ? numberToThaiText(totalAmount) : 'ศูนย์บาทถ้วน';
            
            // Process logo - handle blob/base64/buffer
            let logoSrc = '';
            if (companyInfo.logo) {
                try {
                    let logoData;
                    
                    // Check if it's a Buffer-like object (from database BLOB)
                    if (companyInfo.logo.type === 'Buffer' && Array.isArray(companyInfo.logo.data)) {
                        // Convert Buffer array to base64
                        const uint8Array = new Uint8Array(companyInfo.logo.data);
                        let binary = '';
                        for (let i = 0; i < uint8Array.byteLength; i++) {
                            binary += String.fromCharCode(uint8Array[i]);
                        }
                        logoData = btoa(binary);
                        logoSrc = `data:${companyInfo.logo_type || 'image/png'};base64,${logoData}`;
                    } 
                    // Check if it's already a base64 string
                    else if (typeof companyInfo.logo === 'string') {
                        logoData = companyInfo.logo;
                        
                        if (logoData.startsWith('data:image')) {
                            // Already base64 with prefix
                            logoSrc = logoData;
                        } else if (logoData.startsWith('blob:')) {
                            // Blob URL
                            logoSrc = logoData;
                        } else {
                            // Assume it's base64 without prefix
                            logoSrc = `data:${companyInfo.logo_type || 'image/png'};base64,${logoData}`;
                        }
                    }
                } catch (error) {
                    console.error('Error processing logo:', error);
                    logoSrc = ''; // Fallback to no logo
                }
            }
            
            // Get payment mode with fallback
            const paymentMode = voucherData.payment_mode || voucherData.mode || 'Cash';
            
            // Get reference with fallback
            const referenceNumber = voucherData.reference_id || voucherData.voucher_number || voucherData.reference_number;

            // Helper function within scope
            const getPaymentModeLabel = (mode) => {
                const modes = {
                    'Cash': '💵 เงินสด / Cash',
                    'Bank': '🏦 โอนเงินผ่านธนาคาร / Bank Transfer',
                    'Credit_Card': '💳 บัตรเครดิต / Credit Card',
                    'UPI': '📱 UPI / ยูพีไอ',
                    'Cheque': '📝 เช็ค / Cheque',
                    'Online': '💻 ชำระออนไลน์ / Online Payment'
                };
                return modes[mode] || `💰 ${mode} / ${mode}`;
            };

            const printContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>ใบเสร็จรับเงิน / Receipt</title>
                    <style>
                        @page {
                            size: A4;
                            margin: 10mm 15mm;
                        }
                        
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        
                        body {
                            font-family: 'Sarabun', 'TH Sarabun New', 'Arial Unicode MS', Arial, sans-serif;
                            font-size: 9px;
                            line-height: 1.2;
                            color: #333;
                            background: white;
                            margin: 0;
                            padding: 0;
                        }
                        
                        .receipt-container {
                            max-width: 190mm;
                            margin: 0 auto;
                            padding: 5mm;
                            background: white;
                        }
                        
                        .receipt-header {
                            border: 2px solid #e67e22;
                            border-radius: 4px;
                            padding: 6px;
                            margin-bottom: 8px;
                            text-align: center;
                            background: linear-gradient(135deg, #fef9e7 0%, #fed7aa 100%);
                        }
                        
                        .company-name {
                            font-size: 14px;
                            font-weight: bold;
                            margin-bottom: 3px;
                            color: #d35400;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                        }
                        
                        .company-details {
                            font-size: 8px;
                            line-height: 1.3;
                            color: #495057;
                        }
                        
                        .receipt-title {
                            text-align: center;
                            margin: 6px 0;
                            padding: 6px;
                            border-radius: 4px;
                            background: linear-gradient(135deg, #e67e22 0%, #d35400 100%);
                            color: white;
                        }
                        
                        .title-main {
                            font-size: 13px;
                            font-weight: bold;
                            margin-bottom: 2px;
                        }
                        
                        .title-sub {
                            font-size: 9px;
                            opacity: 0.9;
                        }
                        
                        .section-title {
                            font-size: 10px;
                            font-weight: bold;
                            color: #d35400;
                            margin-bottom: 4px;
                            padding-bottom: 2px;
                            border-bottom: 1px solid #e9ecef;
                        }
                        
                        .receipt-info, .customer-section, .payment-details {
                            margin-bottom: 6px;
                            padding: 5px;
                            background: #fef9e7;
                            border-radius: 3px;
                            border-left: 2px solid #e67e22;
                        }
                        
                        .info-row {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            margin-bottom: 5px;
                            padding: 4px 0;
                            border-bottom: 1px solid #dee2e6;
                        }
                        
                        .info-label {
                            font-weight: 600;
                            color: #495057;
                            min-width: 120px;
                        }
                        
                        .info-value {
                            flex: 1;
                            text-align: right;
                            color: #212529;
                        }
                        
                        .receipt-number {
                            font-weight: bold;
                            color: #d35400;
                            font-family: 'Courier New', monospace;
                        }
                        
                        .customer-name {
                            font-weight: bold;
                            color: #dc3545;
                        }
                        
                        .payment-method {
                            font-weight: bold;
                            color: #28a745;
                        }
                        
                        .reference {
                            font-family: 'Courier New', monospace;
                            background: #e9ecef;
                            padding: 2px 4px;
                            border-radius: 3px;
                        }
                        
                        .amount-section {
                            border: 2px solid #28a745;
                            border-radius: 4px;
                            padding: 6px;
                            margin: 6px 0;
                            background: linear-gradient(135deg, #f8fff8 0%, #e8f5e8 100%);
                        }
                        
                        .amount-header h3 {
                            color: #28a745;
                            margin-bottom: 4px;
                            font-size: 10px;
                        }
                        
                        .amount-row {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            font-size: 11px;
                            margin-bottom: 4px;
                        }
                        
                        .amount-label {
                            font-weight: bold;
                            color: #495057;
                        }
                        
                        .amount-value {
                            font-weight: bold;
                            font-size: 14px;
                            color: #28a745;
                        }
                        
                        .amount-text {
                            margin-top: 4px;
                            padding: 4px;
                            border-radius: 3px;
                            background: rgba(40, 167, 69, 0.1);
                            font-style: italic;
                            color: #495057;
                            text-align: center;
                            border: 1px dashed #28a745;
                            font-size: 8px;
                        }
                        
                        .payment-details {
                            margin: 6px 0;
                        }
                        
                        .signature-section {
                            display: flex;
                            justify-content: space-between;
                            margin-top: 15px;
                            padding: 8px 0;
                        }
                        
                        .signature-box {
                            text-align: center;
                            width: 45%;
                            padding: 5px;
                        }
                        
                        .signature-line {
                            border-top: 2px solid #e67e22;
                            margin-top: 40px;
                            padding-top: 8px;
                            font-weight: bold;
                            color: #495057;
                            background: linear-gradient(135deg, #fef9e7 0%, #fed7aa 100%);
                            border-radius: 3px;
                            padding: 8px;
                            font-size: 9px;
                        }
                        
                        /* Column Layout Styles */
                        .receipt-info-grid {
                            display: grid !important;
                            grid-template-columns: 1fr 1fr !important;
                            gap: 8px !important;
                            margin-bottom: 8px !important;
                            width: 100% !important;
                        }
                        
                        .info-column {
                            background: #fef9e7 !important;
                            padding: 5px !important;
                            border-radius: 3px !important;
                            border-left: 2px solid #e67e22 !important;
                            width: 100% !important;
                            box-sizing: border-box !important;
                            display: block !important;
                        }
                        
                        .column-title {
                            font-size: 9px !important;
                            font-weight: bold !important;
                            color: #d35400 !important;
                            margin-bottom: 3px !important;
                            text-align: center !important;
                            border-bottom: 1px solid #fed7aa !important;
                            padding-bottom: 2px !important;
                        }
                        
                        .compact-info-row {
                            display: flex !important;
                            justify-content: space-between !important;
                            margin-bottom: 2px !important;
                            font-size: 8px !important;
                        }
                        
                        .compact-label {
                            font-weight: 600 !important;
                            color: #d35400 !important;
                            min-width: 50px !important;
                        }
                        
                        .compact-value {
                            color: #212529 !important;
                            text-align: right !important;
                            flex: 1 !important;
                        }
                        
                        .footer {
                            margin-top: 8px;
                            text-align: center;
                            font-size: 7px;
                            color: #6c757d;
                            border-top: 1px solid #dee2e6;
                            padding-top: 5px;
                            background: #f8f9fa;
                            border-radius: 3px;
                            padding: 5px;
                        }
                        
                        .footer p {
                            margin-bottom: 2px;
                        }
                        
                        .tax-notice {
                            background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
                            border: 1px solid #ffc107;
                            border-radius: 3px;
                            padding: 5px;
                            margin: 5px 0;
                            font-size: 7px;
                            text-align: center;
                        }
                        
                        .tax-notice strong {
                            color: #856404;
                        }
                        
                        @media print {
                            body {
                                margin: 0;
                                padding: 0;
                                font-size: 10px;
                            }
                            
                            .receipt-container {
                                max-width: 100%;
                                padding: 5mm;
                                box-shadow: none;
                                min-height: auto;
                            }
                            
                            .receipt-header {
                                background: white !important;
                                border: 1px solid #000 !important;
                                padding: 8px !important;
                            }
                            
                            .receipt-title {
                                background: white !important;
                                color: black !important;
                                border: 1px solid #000;
                                padding: 6px !important;
                            }
                            
                            .amount-section {
                                background: white !important;
                                border: 1px solid #000 !important;
                                padding: 8px !important;
                            }
                            
                            .section-title {
                                color: black !important;
                            }
                            
                            .info-column {
                                background: white !important;
                                border-left: 2px solid #000 !important;
                            }
                            
                            .signature-line {
                                margin-top: 25px !important;
                                border-top: 1px solid #000 !important;
                            }
                            
                            @page {
                                margin: 10mm;
                                size: A4;
                            }
                        }
                    </style>
                </head>
                <body>
                    <div class="receipt-container">
                        <!-- Header with Company Info -->
                        <div class="receipt-header">
                            ${logoSrc ? `
                            <div style="text-align: center; margin-bottom: 5px;">
                                <img src="${logoSrc}" alt="${companyInfo.name || 'Company Logo'}" 
                                     style="max-height: 50px; max-width: 180px; object-fit: contain;" />
                            </div>
                            ` : ''}
                            <div class="company-name">${companyInfo.name || 'ชื่อบริษัท / Company Name'}</div>
                            <div class="company-details">
                                ${companyInfo.address || 'ที่อยู่บริษัท / Company Address'}<br>
                                ${companyInfo.city ? `${companyInfo.city}` : ''} ${companyInfo.state ? `${companyInfo.state}` : ''} ${companyInfo.zip_code ? `${companyInfo.zip_code}` : ''}<br>
                                เลขประจำตัวผู้เสียภาษี / Tax ID: ${companyInfo.tax_id || 'XXXXXXXXXXXXX'}<br>
                                โทรศัพท์ / Tel: ${companyInfo.phone_number || 'XXX-XXX-XXXX'}
                                ${companyInfo.email ? ` | อีเมล: ${companyInfo.email}` : ''}
                            </div>
                        </div>
                        
                        <!-- Receipt Title -->
                        <div class="receipt-title">
                            <div class="title-main">ใบเสร็จรับเงิน</div>
                            <div class="title-sub">PAYMENT RECEIPT</div>
                        </div>
                        
                        <!-- Receipt Information Grid -->
                        <div class="receipt-info-grid">
                            <!-- Column 1: Customer Information -->
                            <div class="info-column">
                                <div class="column-title">ข้อมูลลูกค้า / Customer Info</div>
                                <div class="compact-info-row">
                                    <span class="compact-label">ชื่อ:</span>
                                    <span class="compact-value customer-name">${customerName}</span>
                                </div>
                                <div class="compact-info-row">
                                    <span class="compact-label">ที่อยู่:</span>
                                    <span class="compact-value">${customerAddress}</span>
                                </div>
                                <div class="compact-info-row">
                                    <span class="compact-label">โทร:</span>
                                    <span class="compact-value">${customerPhone}</span>
                                </div>
                            </div>
                            
                            <!-- Column 2: Receipt Details -->
                            <div class="info-column">
                                <div class="column-title">ข้อมูลใบเสร็จ / Receipt Info</div>
                                <div class="compact-info-row">
                                    <span class="compact-label">เลขที่:</span>
                                    <span class="compact-value receipt-number">${voucherData.transaction_id || 'N/A'}</span>
                                </div>
                                <div class="compact-info-row">
                                    <span class="compact-label">วันที่:</span>
                                    <span class="compact-value">${formattedDate}</span>
                                </div>
                                <div class="compact-info-row">
                                    <span class="compact-label">เวลา:</span>
                                    <span class="compact-value">${formattedTime}</span>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Amount Section -->
                        <div class="amount-section">
                            <div class="amount-header">
                                <h3>รายละเอียดการชำระเงิน / Payment Details</h3>
                            </div>
                            ${(hasTDS || hasDiscount) ? `
                            <!-- TDS / Discount Bifurcation -->
                            <div class="amount-row">
                                <span class="amount-label">จำนวนเงินที่ได้รับ / Amount Received:</span>
                                <span class="amount-value">฿ ${amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            ${hasTDS ? `
                            <div class="amount-row">
                                <span class="amount-label" style="color: #f39c12;">หัก TDS / TDS Deducted:</span>
                                <span class="amount-value" style="color: #f39c12;">฿ ${tdsAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            ` : ''}
                            ${hasDiscount ? `
                            <div class="amount-row">
                                <span class="amount-label" style="color: #e53e3e;">ส่วนลด / Discount Given:</span>
                                <span class="amount-value" style="color: #e53e3e;">฿ ${discountAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            ` : ''}
                            <div style="border-top: 2px solid #28a745; margin: 8px 0; padding-top: 8px;">
                                <div class="amount-row">
                                    <span class="amount-label" style="font-size: 16px;">จำนวนเงินรวม / Total Amount:</span>
                                    <span class="amount-value" style="font-size: 20px;">฿ ${totalAmount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                </div>
                            </div>
                            ` : `
                            <div class="amount-row">
                                <span class="amount-label">จำนวนเงิน / Amount:</span>
                                <span class="amount-value">฿ ${amount.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                            `}
                            <div class="amount-text">
                                <strong>(${amountInText})</strong>
                            </div>
                        </div>
                        
                        <!-- Payment Details -->
                        <div class="info-column" style="margin-bottom: 15px;">
                            <div class="column-title">วิธีการชำระเงิน / Payment Method</div>
                            <div class="compact-info-row">
                                <span class="compact-label">ชำระโดย:</span>
                                <span class="compact-value payment-method">${getPaymentModeLabel(paymentMode)}</span>
                            </div>
                            ${referenceNumber ? `
                            <div class="compact-info-row">
                                <span class="compact-label">เลขอ้างอิง:</span>
                                <span class="compact-value reference">${referenceNumber}</span>
                            </div>
                            ` : ''}
                            ${voucherData.remarks ? `
                            <div class="compact-info-row">
                                <span class="compact-label">หมายเหตุ:</span>
                                <span class="compact-value">${voucherData.remarks}</span>
                            </div>
                            ` : ''}
                        </div>
                        
                        <!-- Tax Notice -->
                        <div class="tax-notice">
                            <strong>🏛️ ใบเสร็จรับเงินฉบับนี้ออกตามกฎหมายภาษีอากรของประเทศไทย</strong><br>
                            <strong>🏛️ This receipt is issued in accordance with Thai tax laws</strong>
                        </div>
                        
                        <!-- Signature Section -->
                        <div class="signature-section">
                            <div class="signature-box">
                                <div class="signature-line">
                                    ผู้รับเงิน / Received by
                                </div>
                            </div>
                            <div class="signature-box">
                                <div class="signature-line">
                                    ผู้จ่ายเงิน / Paid by
                                </div>
                            </div>
                        </div>
                        
                        ${companyInfo.bank_name || companyInfo.account_number ? `
                        <!-- Bank Information -->
                        <div class="bank-info" style="background: #f8f9fa; padding: 4px; border-radius: 3px; margin: 4px 0; font-size: 7px; text-align: center; border: 1px solid #dee2e6;">
                            <strong style="color: #2c5282;">🏛️ ข้อมูลบัญชีธนาคาร / Bank Details</strong><br>
                            ${companyInfo.bank_name ? `ธนาคาร: ${companyInfo.bank_name}` : ''}
                            ${companyInfo.account_name ? ` | ชื่อบัญชี: ${companyInfo.account_name}` : ''}
                            ${companyInfo.account_number ? ` | เลขบัญชี: ${companyInfo.account_number}` : ''}
                            ${companyInfo.swift_code ? ` | SWIFT: ${companyInfo.swift_code}` : ''}
                        </div>
                        ` : ''}
                        
                        ${companyInfo.terms_and_conditions ? `
                        <!-- Terms and Conditions -->
                        <div style="background: #fef9e7; padding: 4px; border-radius: 3px; margin: 4px 0; font-size: 6px; color: #495057; border-left: 2px solid #e67e22;">
                            <strong>เงื่อนไข / Terms:</strong> ${companyInfo.terms_and_conditions}
                        </div>
                        ` : ''}
                        
                        <!-- Footer -->
                        <div class="footer">
                            <p><strong>📄 เอกสารออกโดยระบบอัตโนมัติ / Automatically generated</strong></p>
                            <p>📞 ติดต่อสอบถาม / Inquiries: ${companyInfo.phone_number || 'XXX-XXX-XXXX'} | 💻 StoreHub POS</p>
                            <p style="font-size: 8px; color: #adb5bd; margin-top: 3px;">ขอขอบคุณที่ใช้บริการ / Thank you for your business</p>
                        </div>
                    </div>
                </body>
                </html>
            `;

            // Open print window with better handling
            const printWindow = window.open("", "_blank", "width=800,height=600,scrollbars=yes");
            
            if (!printWindow) {
                toast.error("Pop-up blocked! Please allow pop-ups for this site.");
                setLoading(false);
                return;
            }
            
            printWindow.document.write(printContent);
            printWindow.document.close();

            printWindow.onload = () => {
                printWindow.focus();
                setTimeout(() => {
                    printWindow.print();
                    // Optional: Close window after printing
                    printWindow.onafterprint = () => {
                        printWindow.close();
                    };
                }, 500);
            };

            // Handle print window errors
            printWindow.onerror = (error) => {
                console.error("Print window error:", error);
                toast.error("Error opening print window");
            };

            setLoading(false);
            toast.success("Receipt generated successfully!");
        } catch (error) {
            console.error("Error printing receipt:", error);
            toast.error("Error generating receipt");
            setLoading(false);
        }
    };

    // Helper function to convert number to Thai text
    const numberToThaiText = (number) => {
        const digits = ['ศูนย์', 'หนึ่ง', 'สอง', 'สาม', 'สี่', 'ห้า', 'หก', 'เจ็ด', 'แปด', 'เก้า'];
        const positions = ['', 'สิบ', 'ร้อย', 'พัน', 'หมื่น', 'แสน', 'ล้าน'];
        
        if (number === 0) return 'ศูนย์บาทถ้วน';
        
        const [intPart, decPart] = number.toFixed(2).split('.');
        let result = '';
        
        // Convert integer part
        const intStr = intPart.toString();
        const len = intStr.length;
        
        for (let i = 0; i < len; i++) {
            const digit = parseInt(intStr[i]);
            const position = len - i - 1;
            
            if (digit === 0) continue;
            
            if (position === 1 && digit === 1) {
                result += 'สิบ';
            } else if (position === 1 && digit === 2) {
                result += 'ยี่สิบ';
            } else if (position === 0 && digit === 1 && len > 1) {
                result += 'เอ็ด';
            } else {
                result += digits[digit] + positions[position];
            }
        }
        
        result += 'บาท';
        
        // Convert decimal part
        if (parseInt(decPart) === 0) {
            result += 'ถ้วน';
        } else {
            const satangStr = decPart.toString();
            let satangText = '';
            
            for (let i = 0; i < satangStr.length; i++) {
                const digit = parseInt(satangStr[i]);
                const position = satangStr.length - i - 1;
                
                if (digit === 0 && position === 1) continue;
                
                if (position === 1 && digit === 1) {
                    satangText += 'สิบ';
                } else if (position === 1 && digit === 2) {
                    satangText += 'ยี่สิบ';
                } else if (position === 0 && digit === 1 && satangStr.length > 1) {
                    satangText += 'เอ็ด';
                } else {
                    satangText += digits[digit];
                    if (position === 1) satangText += 'สิบ';
                }
            }
            
            result += satangText + 'สตางค์';
        }
        
        return result;
    };
    useEffect(() => {
        const fetchAndSetData = async () => {
            try {
                const result = await fetchData("receipt_vouchers", setData, "id", {});
                console.log("Fetched receipt vouchers data:", result);
                if (result && result.length > 0) {
                    console.log("Sample record structure:", result[0]);
                    console.log("Available fields:", Object.keys(result[0]));
                }
            } catch (error) {
                console.error("Error in useEffect:", error);
            }
        };

        fetchAndSetData();
    }, []);

    // Filter data based on search and filter mode
    const filteredData = data.filter(item => {
        const matchesSearch = 
            item.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.customer_id?.toString().includes(searchTerm) ||
            (item.amount || item.amount_paid || 0)?.toString().includes(searchTerm);
        
        const matchesFilter = filterMode === "All" || (item.payment_mode || item.mode) === filterMode;
        
        return matchesSearch && matchesFilter;
    });

    // Export filtered data to Excel (.xlsx)
    const exportExcel = () => {
        if (filteredData.length === 0) {
            toast.error("No records to export");
            return;
        }
        const worksheetData = filteredData.map(item => {
            const customer = customers.find(c => c.id === parseInt(item.customer_id));
            const customerName = customer ? customer.name : (item.customer_name || `Customer #${item.customer_id || 'Unknown'}`);
            return {
                "Txn ID": item.transaction_id,
                "Customer": customerName,
                "Date": item.receipt_date || item.payment_date ? new Date(item.receipt_date || item.payment_date).toLocaleDateString('th-TH') : '',
                "Mode": item.payment_mode || item.mode || '',
                "Amount": parseFloat(item.amount || item.amount_paid || 0) || 0,
                "TDS Amount": parseFloat(item.tds_amount || 0) || 0,
                "Discount Amount": parseFloat(item.discount_amount || 0) || 0,
                "Ref ID": item.reference_id || item.voucher_number || '',
            };
        });
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Receipt Vouchers");
        XLSX.writeFile(workbook, `receipt_vouchers_${dayjs().format('YYYY-MM-DD')}.xlsx`);
    };

    // Export filtered data to PDF
    const exportPDF = () => {
        if (filteredData.length === 0) {
            toast.error("No records to export");
            return;
        }
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.setFontSize(14);
        doc.text(companyInfo.name || 'Receipt Vouchers', 14, 15);

        const tableColumn = ["Txn ID", "Customer", "Date", "Mode", "Amount", "TDS Amount", "Discount Amount", "Ref ID"];
        const tableRows = filteredData.map(item => {
            const customer = customers.find(c => c.id === parseInt(item.customer_id));
            const customerName = customer ? customer.name : (item.customer_name || `Customer #${item.customer_id || 'Unknown'}`);
            return [
                item.transaction_id || '',
                customerName,
                item.receipt_date || item.payment_date ? new Date(item.receipt_date || item.payment_date).toLocaleDateString('th-TH') : '',
                item.payment_mode || item.mode || '',
                (parseFloat(item.amount || item.amount_paid || 0) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }),
                (parseFloat(item.tds_amount || 0) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }),
                (parseFloat(item.discount_amount || 0) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 }),
                item.reference_id || item.voucher_number || '-',
            ];
        });

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 22,
        });

        doc.save(`receipt_vouchers_${dayjs().format('YYYY-MM-DD')}.pdf`);
    };

    // Table columns for Payment History
    const tableColumns = [
        { title: 'Txn ID', dataIndex: 'transaction_id', key: 'transaction_id', render: (v) => <Text strong>{v}</Text> },
        {
            title: 'Customer',
            key: 'customer',
            render: (_, item) => {
                const customer = customers.find(c => c.id === parseInt(item.customer_id || item.customer_name));
                return customer ? customer.name : (item.customer_name || `Customer #${item.customer_id || 'Unknown'}`);
            }
        },
        {
            title: 'Date',
            key: 'date',
            render: (_, item) => {
                const d = item.receipt_date || item.payment_date;
                return d ? new Date(d).toLocaleDateString('th-TH') : 'Invalid Date';
            },
            sorter: (a, b) => new Date(a.receipt_date || a.payment_date || 0) - new Date(b.receipt_date || b.payment_date || 0),
        },
        {
            title: 'Mode',
            key: 'mode',
            render: (_, item) => {
                const mode = item.payment_mode || item.mode || 'Unknown';
                const color = mode === 'Cash' ? 'green' : mode === 'Bank' ? 'blue' : mode === 'Cheque' ? 'orange' : 'cyan';
                return <Tag color={color}>{mode}</Tag>;
            }
        },
        {
            title: 'Amount',
            key: 'amount',
            render: (_, item) => <Text strong>฿ {(parseFloat(item.amount || item.amount_paid || 0) || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</Text>,
            sorter: (a, b) => (parseFloat(a.amount || a.amount_paid || 0) || 0) - (parseFloat(b.amount || b.amount_paid || 0) || 0),
        },
        {
            title: 'TDS Amount',
            key: 'tds_amount',
            render: (_, item) => item.tds_amount && parseFloat(item.tds_amount) > 0
                ? <Text style={{ color: '#d48806' }}>฿{parseFloat(item.tds_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</Text>
                : '-'
        },
        {
            title: 'Discount Amount',
            key: 'discount_amount',
            render: (_, item) => item.discount_amount && parseFloat(item.discount_amount) > 0
                ? <Text style={{ color: '#cf1322' }}>฿{parseFloat(item.discount_amount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</Text>
                : '-'
        },
        { title: 'Ref ID', key: 'ref_id', render: (_, item) => item.reference_id || item.voucher_number || '-' },
        {
            title: 'Action',
            key: 'action',
            align: 'center',
            render: (_, item) => (
                <Tooltip title="Print Receipt">
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<PrinterOutlined />}
                        onClick={() => handlePrintReceipt(item)}
                    />
                </Tooltip>
            )
        },
    ];

    return (
        <Layout>
            <Header title="Receipt Vouchers / ใบเสร็จรับเงิน" />
            <ToastContainer />

            {loading && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '30px',
                        borderRadius: '10px',
                        textAlign: 'center'
                    }}>
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div>Processing...</div>
                    </div>
                </div>
            )}

            {/* Top bar: title + Create Receipt Voucher button */}
            <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                <Col>
                    <Text type="secondary">Record customer payments and print receipts</Text>
                </Col>
                <Col>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setShowCreateModal(true)}
                    >
                        Create Receipt Voucher
                    </Button>
                </Col>
            </Row>

            {/* Create Receipt Voucher Modal */}
            <Modal
                title={
                    <Space>
                        <FileTextOutlined style={{ color: '#0d9488' }} />
                        Create Receipt Voucher
                    </Space>
                }
                open={showCreateModal}
                onCancel={() => setShowCreateModal(false)}
                footer={null}
                width={680}
            >
                <form onSubmit={handleSubmit} className="voucher-form">
                    {/* Date + Customer - two columns per row to keep the form compact */}
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <CalendarOutlined className="me-2" />
                                    Date of Payment Received <span className="text-danger">*</span>
                                </label>
                                <DatePicker
                                    value={voucherDate}
                                    onChange={(date) => setVoucherDate(date || dayjs())}
                                    style={{ width: '100%' }}
                                    format="DD-MM-YYYY"
                                    allowClear={false}
                                />
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <UserOutlined className="me-2" />
                                    Customer Name <span className="text-danger">*</span>
                                </label>
                                <Select
                                    value={selectedCustomer || undefined}
                                    onChange={(value) => getOutStandingBalance(value)}
                                    placeholder="-- Select Customer --"
                                    style={{ width: '100%' }}
                                    showSearch
                                    optionFilterProp="children"
                                >
                                    {customers.map((customer) => (
                                        <Option key={customer.id} value={customer.id}>
                                            {customer.name}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <DollarOutlined className="me-2" />
                                    Outstanding Amount
                                </label>
                                <Input
                                    value={`฿ ${outstandingAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <CreditCardOutlined className="me-2" />
                                    Payment Mode <span className="text-danger">*</span>
                                </label>
                                <Select
                                    value={paymentMode || undefined}
                                    onChange={(value) => {
                                        setPaymentMode(value);
                                        setBankAccountId(''); // Reset bank account when mode changes
                                    }}
                                    placeholder="-- Select Payment Mode --"
                                    style={{ width: '100%' }}
                                >
                                    {paymentModes.map(mode => (
                                        <Option key={mode.value} value={mode.value}>
                                            {mode.label}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                    </Row>

                    {/* Bank Account Selection - Only show when bank is required */}
                    {paymentMode && paymentModes.find(mode => mode.value === paymentMode)?.requiresBank && (
                        <Row gutter={16}>
                            <Col xs={24}>
                                <div className="form-group mb-3">
                                    <label className="form-label d-flex align-items-center">
                                        <CreditCardOutlined className="me-2" />
                                        Bank Account <span className="text-danger">*</span>
                                    </label>
                                    <Select
                                        value={bankAccountId || undefined}
                                        onChange={(value) => setBankAccountId(value)}
                                        placeholder="Select Bank Account"
                                        style={{ width: '100%' }}
                                    >
                                        {bankAccounts.filter(acc => acc.is_active).map(account => (
                                            <Option key={account.id} value={account.id}>
                                                {account.account_name} ({account.bank_name}) - ฿{account.current_balance?.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* TDS Receivable Checkbox */}
                    <Row gutter={16}>
                        <Col xs={24}>
                            <div className="form-group mb-3">
                                <Checkbox
                                    checked={isTdsReceivable}
                                    onChange={(e) => {
                                        setIsTdsReceivable(e.target.checked);
                                        if (!e.target.checked) {
                                            setTdsAmount('');
                                        }
                                    }}
                                >
                                    Customer Deducted TDS (Tax Deducted at Source)
                                </Checkbox>
                                <br />
                                <small className="text-muted">Check this if customer deducted TDS from payment</small>
                            </div>
                        </Col>
                    </Row>

                    {/* TDS Amount - Only show when checkbox is checked */}
                    {isTdsReceivable && (
                        <Row gutter={16}>
                            <Col xs={24}>
                                <div className="form-group mb-3">
                                    <label className="form-label d-flex align-items-center">
                                        <DollarOutlined className="me-2" />
                                        TDS Amount (Deducted by Customer) <span className="text-danger">*</span>
                                    </label>
                                    <InputNumber
                                        min={0}
                                        step={0.01}
                                        value={tdsAmount}
                                        onChange={(value) => setTdsAmount(value)}
                                        style={{ width: '100%' }}
                                        placeholder="0.00"
                                    />
                                    <small className="text-muted">This amount will be added to outstanding balance</small>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* TDS Calculation Summary */}
                    {isTdsReceivable && tdsAmount && paymentAmount && (
                        <Row gutter={16}>
                            <Col xs={24}>
                                <div className="mb-3" style={{
                                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e8f4f8 100%)',
                                    border: '1px solid #d4e7f0',
                                    borderRadius: '8px',
                                    padding: '12px 15px'
                                }}>
                                    <strong style={{ color: '#2c5282' }}>📊 TDS Summary:</strong><br/>
                                    <small style={{ color: '#4a5568' }}>
                                        • Amount Received: ฿{parseFloat(paymentAmount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}<br/>
                                        • TDS Deducted: ฿{parseFloat(tdsAmount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}<br/>
                                        • Total Invoice Amount: ฿{(parseFloat(paymentAmount) + parseFloat(tdsAmount)).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                                    </small>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Discount Given Checkbox */}
                    <Row gutter={16}>
                        <Col xs={24}>
                            <div className="form-group mb-3">
                                <Checkbox
                                    checked={isDiscountGiven}
                                    onChange={(e) => {
                                        setIsDiscountGiven(e.target.checked);
                                        if (!e.target.checked) {
                                            setDiscountAmount('');
                                        }
                                    }}
                                >
                                    Give Discount to Customer
                                </Checkbox>
                                <br />
                                <small className="text-muted">Check this if a discount is being waived off from the outstanding balance</small>
                            </div>
                        </Col>
                    </Row>

                    {/* Discount Amount - Only show when checkbox is checked */}
                    {isDiscountGiven && (
                        <Row gutter={16}>
                            <Col xs={24}>
                                <div className="form-group mb-3">
                                    <label className="form-label d-flex align-items-center">
                                        <DollarOutlined className="me-2" />
                                        Discount Amount <span className="text-danger">*</span>
                                    </label>
                                    <InputNumber
                                        min={0}
                                        step={0.01}
                                        value={discountAmount}
                                        onChange={(value) => setDiscountAmount(value)}
                                        style={{ width: '100%' }}
                                        placeholder="0.00"
                                    />
                                    <small className="text-muted">This amount will be waived off and deducted from the outstanding balance</small>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Discount Calculation Summary */}
                    {isDiscountGiven && discountAmount && paymentAmount && (
                        <Row gutter={16}>
                            <Col xs={24}>
                                <div className="mb-3" style={{
                                    background: 'linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)',
                                    border: '1px solid #feb2b2',
                                    borderRadius: '8px',
                                    padding: '12px 15px'
                                }}>
                                    <strong style={{ color: '#c53030' }}>🏷️ Discount Summary:</strong><br/>
                                    <small style={{ color: '#4a5568' }}>
                                        • Amount Received: ฿{parseFloat(paymentAmount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}<br/>
                                        • Discount Given: ฿{parseFloat(discountAmount).toLocaleString('th-TH', { minimumFractionDigits: 2 })}<br/>
                                        • Total Balance Cleared: ฿{(parseFloat(paymentAmount) + parseFloat(discountAmount)).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
                                    </small>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* Amount Paid + Reference Number */}
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label d-flex align-items-center">
                                    <DollarOutlined className="me-2" />
                                    {isTdsReceivable ? 'Amount Received (After TDS)' : 'Amount Paid'} <span className="text-danger">*</span>
                                </label>
                                <InputNumber
                                    min={0}
                                    step={0.01}
                                    value={paymentAmount}
                                    onChange={(value) => setPaymentAmount(value)}
                                    style={{ width: '100%' }}
                                    placeholder="0.00"
                                />
                                {isTdsReceivable && (
                                    <small className="text-info d-block mt-1">
                                        💡 Amount received after TDS deduction
                                    </small>
                                )}
                            </div>
                        </Col>
                        <Col xs={24} md={12}>
                            <div className="form-group mb-3">
                                <label className="form-label">
                                    Reference Number (Optional)
                                </label>
                                <Input
                                    value={referenceNumber}
                                    onChange={(e) => setReferenceNumber(e.target.value)}
                                    placeholder="Enter reference number"
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* Remarks */}
                    <Row gutter={16}>
                        <Col xs={24}>
                            <div className="form-group mb-3">
                                <label className="form-label">Remarks (Optional)</label>
                                <Input.TextArea
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    rows={2}
                                    placeholder="Enter any additional notes"
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* Balance After Payment */}
                    <Row gutter={16}>
                        <Col xs={24}>
                            <div className="form-group mb-4">
                                <label className="form-label d-flex align-items-center">
                                    <DollarOutlined className="me-2" />
                                    Balance After Payment
                                </label>
                                <Input
                                    value={`฿ ${balanceAfterPayment.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                                    readOnly
                                    disabled
                                    style={{ fontWeight: 'bold' }}
                                />
                            </div>
                        </Col>
                    </Row>

                    {/* Submit Button */}
                    <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        block
                        loading={loading}
                        icon={<FileTextOutlined />}
                    >
                        {loading ? 'Processing...' : 'Save Payment'}
                    </Button>
                </form>
            </Modal>

            {/* Payment History Table */}
            <Card
                title={
                    <Space>
                        <FileTextOutlined style={{ color: '#0d9488' }} />
                        <Text strong>Payment History</Text>
                    </Space>
                }
                extra={
                    <Space>
                        <Button icon={<FileExcelOutlined />} onClick={exportExcel}>Excel</Button>
                        <Button danger icon={<FilePdfOutlined />} onClick={exportPDF}>PDF</Button>
                    </Space>
                }
            >
                {/* Search and Filter Section */}
                <Row gutter={12} className="mb-3">
                    <Col xs={24} md={10} className="mb-2">
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder="Search by Transaction ID, Customer ID, Amount..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} md={6} className="mb-2">
                        <Select
                            value={filterMode}
                            onChange={(value) => setFilterMode(value)}
                            style={{ width: '100%' }}
                        >
                            <Option value="All">All Payment Modes</Option>
                            <Option value="Cash">Cash</Option>
                            <Option value="Bank">Bank</Option>
                            <Option value="Cheque">Cheque</Option>
                            <Option value="Online">Online</Option>
                        </Select>
                    </Col>
                </Row>

                {/* Table */}
                <Table
                    rowKey="id"
                    columns={tableColumns}
                    dataSource={filteredData}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '25', '50', '100'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                    }}
                    scroll={{ x: true }}
                />

                {/* Summary */}
                <Row gutter={16} className="mt-3 p-3 bg-light border rounded">
                    <Col xs={24} md={8} className="text-center">
                        <Statistic title="Total Records" value={filteredData.length} valueStyle={{ color: '#0d9488' }} />
                    </Col>
                    <Col xs={24} md={8} className="text-center">
                        <Statistic
                            title="Total Amount"
                            value={filteredData.reduce((sum, item) => sum + (parseFloat(item.amount || item.amount_paid || 0) || 0), 0)}
                            precision={2}
                            prefix="฿"
                            valueStyle={{ color: '#059669' }}
                        />
                    </Col>
                    <Col xs={24} md={8} className="text-center">
                        <Statistic
                            title="Latest Payment"
                            value={filteredData.length > 0 && (filteredData[0].receipt_date || filteredData[0].payment_date) ? new Date(filteredData[0].receipt_date || filteredData[0].payment_date).toLocaleDateString('th-TH') : '-'}
                            valueStyle={{ color: '#2563eb', fontSize: 20 }}
                        />
                    </Col>
                </Row>
            </Card>
        </Layout>
    );
}
